import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React from 'react';
import { isMacOs } from '../../helpers/is-mac-os';
import './styles.css';

/** Maps key part names (lowercase) to their display symbols. */
const KEY_SYMBOLS: Record<string, string> = {
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  command: '⌘',
  enter: '↵',
};

/** Modifier key names that trigger GitHub-style unified <kbd> display. */
const MODIFIER_KEYS = new Set(['command', 'ctrl', 'control', 'alt', 'option', 'shift', 'meta']);

/**
 * Given a comma-separated key string (e.g. "ctrl+arrowup,command+arrowup"), returns the segment
 * to display for the current platform: the one containing "command" on macOS, or "ctrl" otherwise.
 * If there is only one segment, returns it as-is.
 */
export function getTriggerForDisplay(keyString: string): string {
  const segments = keyString.split(',').map((s) => s.trim());

  if (segments.length <= 1) {
    return keyString.trim();
  }

  const mac = isMacOs();
  const chosen = segments.find((s) =>
    mac ? s.toLowerCase().includes('command') : s.toLowerCase().includes('ctrl'),
  );

  return chosen ?? segments[0];
}

/** Splits a trigger string into trimmed key parts (e.g. "command+enter" → ["command", "enter"]). */
function getRawParts(trigger: string): string[] {
  return trigger
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Maps a raw key part to its display label (symbol or uppercase). */
function formatKeyPart(part: string): string {
  const symbol = KEY_SYMBOLS[part.toLowerCase()];
  return symbol ?? part.toUpperCase();
}

/** True when any part of the combo is a modifier (command, ctrl, shift, etc.). */
export function hasModifierKey(parts: string[]): boolean {
  return parts.some((part) => MODIFIER_KEYS.has(part.toLowerCase()));
}

/**
 * Returns the trigger as plain text for tooltips where styled kbd is not desired.
 * With a modifier: GitHub-style "⌘ ↵"; without: "A + B".
 */
export function formatTriggerAsText(trigger: string): string {
  const rawParts = getRawParts(trigger);
  if (rawParts.length === 0) return '';
  const labels = rawParts.map(formatKeyPart);
  return hasModifierKey(rawParts) ? labels.join(' ') : labels.join(' + ');
}

export type HotkeyDisplayVariant = 'rich' | 'text';

export interface HotkeyDisplayProps {
  /** Key string (e.g. "shift+l" or "ctrl+arrowup,command+arrowup"); platform-appropriate segment is chosen internally. */
  readonly hotkey: string;
  /** "rich" renders styled <kbd> parts; "text" renders plain text via formatTriggerAsText. */
  readonly variant?: HotkeyDisplayVariant;
  /** Merged after the default layout styles; later entries win (MUI sx array merge). */
  readonly sx?: SxProps<Theme>;
}

/**
 * Renders a single hotkey string. With variant "rich" (default):
 * - Combo with a modifier → one unified <kbd> (GitHub style), e.g. <kbd>⌘ ↵</kbd>
 * - No modifier → separate <kbd> parts joined by "+", e.g. <kbd>A</kbd> + <kbd>B</kbd>
 * With variant "text", shows plain text via formatTriggerAsText.
 * Accepts raw key strings (including comma-separated platform variants); picks the right segment via getTriggerForDisplay.
 */
export function HotkeyDisplay({ hotkey, variant = 'rich', sx }: HotkeyDisplayProps) {
  const displayTrigger = getTriggerForDisplay(hotkey);

  if (variant === 'text') {
    const text = formatTriggerAsText(displayTrigger);
    return text ? (
      <Box component="span" sx={sx}>
        {text}
      </Box>
    ) : null;
  }

  const rawParts = getRawParts(displayTrigger);
  if (rawParts.length === 0) return null;

  const labels = rawParts.map(formatKeyPart);

  // Modifier combos: single kbd with space-separated keys (e.g. "⌘ ↵").
  if (hasModifierKey(rawParts)) {
    return (
      <Box
        component="span"
        sx={[{ display: 'inline-flex', alignItems: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <kbd className="kbd-light">{labels.join(' ')}</kbd>
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={[
        { display: 'inline-flex', alignItems: 'center', gap: 0.5 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {labels.map((display, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <Box component="span" sx={{ color: 'inherit' }}>
              +
            </Box>
          )}
          <kbd className="kbd-light">{display}</kbd>
        </React.Fragment>
      ))}
    </Box>
  );
}
